import {chain} from 'lodash';
import {createSelector} from 'reselect';

import {compareName} from '../../../../srcCommon/helpers/localeHelpers';
import {AppParametricSelector} from '../../../helpers/reselectHelpers';
import {AnswerFolderModel} from '../../../models/api/answerFolderModel';
import {AppState} from '../../appState';
import {AsyncStatus} from '../../appTypes';

export function getAnswerFolders(state: AppState) {
  return state.data.answerFolders.byId;
}

export function getAnswerFoldersAsyncStatus(state: AppState): AsyncStatus {
  return state.data.answerFolders;
}

export function makeGetAnswerFolder<P>(getAnswerFolderId: AppParametricSelector<P, number | undefined>) {
  return createSelector([getAnswerFolders, getAnswerFolderId], (answerFolders, id) =>
    id ? answerFolders[id] : undefined,
  );
}

/**
 *  INPUT:
 *      answerFolder = {
 *        name: "Baz"
 *        folderPath: [
 *          123, // Corresponds to folder "Foo"
 *          456, // Corresponds to folder "Bar"
 *        ],
 *        ...
 *      }
 *
 *  OUTPUT: ["Foo", "Bar", "Baz"]
 * */
export function makeGetAnswerFolderPathNames<P>(
  getAnswerFolder: AppParametricSelector<P, AnswerFolderModel>,
) {
  return createSelector([getAnswerFolders, getAnswerFolder], (answerFolders, answerFolder) => [
    ...chain(answerFolder.folderPath)
      .map((folderId) => answerFolders[folderId]?.name)
      .compact()
      .value(),
    answerFolder.name,
  ]);
}

export function makeGetAnswerFoldersInNamespaces<P>(
  getNamespaces: AppParametricSelector<P, ReadonlyArray<string>>,
): AnswerFoldersSelector<P> {
  return createSelector([getFlatAnswerFolders, getNamespaces], (answerFolders, namespaces) =>
    answerFolders.filter((folder) => namespaces.includes(folder.namespace)),
  );
}
export type AnswerFoldersSelector<P> = AppParametricSelector<P, AnswerFolderModel[]>;

export const getFlatAnswerFolders = createSelector(getAnswerFolders, (answerFolders) =>
  chain(answerFolders).values().compact().value(),
);

// Folders are sorted in alphabetical order across all namespaces.
export const getSortedAnswerFolders = createSelector(getFlatAnswerFolders, (folders) =>
  folders.sort((a, b) => compareName(a.name, b.name)),
);
